import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { Box, Typography, Select, MenuItem } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { fetchStockDashboardData } from "../../../../redux/reducers/stockDashboardSlice";
const Stockbar = () => {
  const dispatch = useDispatch();
  const { expiryTracking, stockTurnOverRate, loading } = useSelector(
    (state) => state.stockDashboard
  );
  const [selectedYear, setSelectedYear] = useState("2025");
  const [expiryData, setExpiryData] = useState([]);
  const [turnoverData, setTurnoverData] = useState([]);

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchStockDashboardData());
  }, [dispatch]);

  // Process expiry tracking data
  useEffect(() => {
    if (!Array.isArray(expiryTracking)) return;

    const topExpiry = (expiryTracking || []).slice(0, 8);
    // const processed = topExpiry.map((item, index) => ({
    //   name: item.tradeName,
    //   value: item.daysRemaining,
    //   label: `Expiry Date: ${item.expiryDate}\nBatch No: ${item.batchNo},\nDays Remaining: ${item.daysRemaining}`,
    //   days: item.daysRemaining,
    //   color: getBarColor(item.daysRemaining, index),
    // }));
    const processed = topExpiry.map((item) => ({
  name: item.tradeName,
  value: item.daysRemaining,
  label: `Expiry Date: ${item.expiryDate}\nBatch No: ${item.batchNo},\nDays Remaining: ${item.daysRemaining}`,
  days: item.daysRemaining,
  color: getBarColor(item.daysRemaining),  // ✅ Use new simple color logic
}));

    setExpiryData(processed);
  }, [expiryTracking]);

  // Process turnover data based on selected year
  useEffect(() => {
    if (!stockTurnOverRate || !stockTurnOverRate[selectedYear]) return;

    const turnoverRaw = stockTurnOverRate[selectedYear];
    setTurnoverData(Array.isArray(turnoverRaw) ? turnoverRaw : []);
  }, [stockTurnOverRate, selectedYear]);

  // Bar Color Logic
const getBarColor = (daysRemaining) => {
  if (daysRemaining <= 15) return "#EF4444";   
  if (daysRemaining <= 30) return "#F59E0B";    
  return "#22C55E";                             
};

  // Expiry Chart Options
  const expiryChartOptions = {
    chart: { 
      type: "bar", 
      toolbar: { show: false },
      offsetY: 10,
      offsetX: 10
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "60%",
        distributed: true,
        borderRadius: 6,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        return `(${expiryData[opts.dataPointIndex]?.days} days)`;
      },
      offsetX: 180,
      style: {
        fontSize: "12px",
        fontWeight: "bold",
      },
    },
    xaxis: {
      categories: expiryData.map((d) => d.name),
      max: 100,
      tickAmount: 5,
      labels: {
        show: true,
        style: { fontSize: "12px", colors: "#2C2C49" },
      },
    },
    yaxis: {
      labels: {
        show: true,
        maxWidth: 150,
        style: { fontSize: "12px", colors: "#2C2C49", fontWeight: "500" },
        formatter: function (val) {
          return val && val.length > 20 ? val.substring(0, 17) + "..." : val;
        }
      },
      tickAmount: expiryData.length,
    },
    grid: { 
      show: true, 
      strokeDashArray: 4,
      padding: { left: 20, right: 20 }
    },
    colors: expiryData.map((d) => d.color),
    // fill: {
    //   type: "gradient",
    //   gradient: {
    //     shade: "light",
    //     type: "horizontal",
    //     shadeIntensity: 0.3,
    //     gradientToColors: expiryData.map((d) => d.color + "80"),
    //     inverseColors: false,
    //     opacityFrom: 0.9,
    //     opacityTo: 0.7,
    //     stops: [0, 100]
    //   }
    // },
    fill: {
  type: "solid",           // ✅ No gradient
},
    tooltip: {
      enabled: true,
      custom: function ({ dataPointIndex }) {
        const item = expiryData[dataPointIndex];
        return `<div style="padding: 10px; background: #fff; border: 1px solid #ccc; border-radius: 4px;">
                  <strong>${item.name}</strong><br/>
                  ${item.label.replace(/\n/g, "<br/>")}
                </div>`;
      },
    },
    responsive: [{
      breakpoint: 768,
      options: {
        yaxis: {
          labels: {
            maxWidth: 100,
            formatter: function (val) {
              return val && val.length > 15 ? val.substring(0, 12) + "..." : val;
            }
          }
        }
      }
    }]
  };

  const expiryChartSeries = [
    {
      data: expiryData.map((d) => d.value),
    },
  ];

  // Turnover Chart Options
  const turnoverChartOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
    },
    stroke: {
      width: 2,
      curve: "smooth",
    },
    xaxis: {
      categories: turnoverData.map((item) => item.month),
    },
    legend: { show: true },
    markers: { size: 4 },
    yaxis: {
      labels: {
        formatter: (val) => `₹${val.toLocaleString()}`,
      },
    },
  };

  const turnoverChartSeries = [
    {
      name: "Turnover",
      data: turnoverData.map((item) => item.total),
    },
  ];

  if (loading && !expiryData.length && !turnoverData.length)
    return <Box p={3}>Loading charts...</Box>;

  return (
    <Box display="flex" gap={3} justifyContent="space-between" p={3} sx={{ flexWrap: "nowrap" }}>
      {/* Expiry Tracking */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: 2,
          padding: 3,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Expiry Tracking
          </Typography>
        </Box>
        <Chart options={expiryChartOptions} series={expiryChartSeries} type="bar" height={350} />
      </Box>

      {/* Stock Turnover */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: 2,
          padding: 3,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Stock Turnover Rate
          </Typography>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            size="small"
            variant="outlined"
          >
            {[ "2024", "2025"].map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Chart options={turnoverChartOptions} series={turnoverChartSeries} type="line" height={280} />
      </Box>
    </Box>
  );
};

export default Stockbar;
